"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createApiBuilder = exports.createMcAuthProxyMiddleware = exports.createAuthMiddlewareWithExistingTokenWithRollingSuffix = void 0;
var version_1 = require("../lib/version");
var platform_sdk_1 = require("@commercetools/platform-sdk");
var sdk_client_v2_1 = require("@commercetools/sdk-client-v2");
var debug_1 = __importDefault(require("debug"));
var isomorphic_fetch_1 = __importDefault(require("isomorphic-fetch"));
var debug = (0, debug_1.default)('nuts:ad:api');
var DEFAULT_AUTH_HOST = 'https://auth.us-central1.gcp.commercetools.com';
var DEFAULT_API_HOST = 'https://api.us-central1.gcp.commercetools.com';
function acceptJsonMiddleware(next) {
    return function (request, response) {
        var _a, _b;
        if (!((_a = request.headers) === null || _a === void 0 ? void 0 : _a.Accept) || ((_b = request.headers) === null || _b === void 0 ? void 0 : _b.Accept) === '*/*') {
            request.headers = __assign(__assign({}, request.headers), { Accept: 'application/json' });
        }
        next(request, response);
    };
}
function createAuthMiddlewareWithExistingTokenWithRollingSuffix(authorization, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.interval, interval = _c === void 0 ? 1 : _c;
    return function (next) { return function (request, response) {
        // The same suffix will be used for `lifetime` ms
        var suffix = Math.floor(Date.now() / interval);
        var requestWithAuth = __assign(__assign({}, request), { headers: __assign(__assign({}, request.headers), { Authorization: "".concat(authorization, ":").concat(suffix) }) });
        return next(requestWithAuth, response);
    }; };
}
exports.createAuthMiddlewareWithExistingTokenWithRollingSuffix = createAuthMiddlewareWithExistingTokenWithRollingSuffix;
/**
 * See https://docs.commercetools.com/custom-applications/main-concepts/proxy-to-external-api
 */
function createMcAuthProxyMiddleware(config) {
    var forwardTo = config.forwardTo.replace(/\/$/, '');
    return function (next) { return function (request, response) {
        var _a;
        if ((_a = request.uri) === null || _a === void 0 ? void 0 : _a.startsWith("/".concat(config.projectKey).concat(config.path))) {
            var forwardUri = forwardTo + request.uri.substring(config.projectKey.length + 1);
            return next(__assign(__assign({}, request), { uri: '/proxy/forward-to', headers: __assign(__assign({}, request.headers), { 'X-Forward-To': forwardUri, 'X-Project-Key': config.projectKey }) }), response);
        }
        return next(request, response);
    }; };
}
exports.createMcAuthProxyMiddleware = createMcAuthProxyMiddleware;
/**
 * Set up a CT SDK client with appropriate middleware
 *
 * - `projectKey` is always required
 * - `accessToken` optionally authenticate using Bearer access token
 * - `auth` optionally authenticate using auth.(host, clientId, clientSecret)
 * - `host` optionally override default API host
 *
 * If neither `accessToken` nor `auth` is provided this will assume that an
 * available `mcAccessToken` cookie will be sufficient. All other CT
 * connection and authentication options can be provided as needed.
 */
function createApiBuilder(_a) {
    var _b, _c, _d, _e, _f;
    var accessToken = _a.accessToken, api = _a.api, auth = _a.auth, topProjectKey = _a.projectKey, shoppingListServiceProxy = _a.shoppingListServiceProxy, httpOptions = __rest(_a, ["accessToken", "api", "auth", "projectKey", "shoppingListServiceProxy"]);
    debug("auto-delivery-sdk@".concat(version_1.version));
    var projectKey = (_c = (_b = topProjectKey !== null && topProjectKey !== void 0 ? topProjectKey : auth === null || auth === void 0 ? void 0 : auth.projectKey) !== null && _b !== void 0 ? _b : process.env.CT_PROJECT_KEY) !== null && _c !== void 0 ? _c : process.env.VUE_APP_PROJECT_KEY;
    if (!projectKey) {
        throw new Error('projectKey is required');
    }
    var middlewares = [];
    if (accessToken) {
        debug('Using access token authentication');
        // Rolling number appended only because CT makes it hard to prevent caching
        // of our OAuth introspection endpoint's response. Our workaround is to give
        // responses indicating 120 second token lifetime, and to synthesize
        // "different" tokens every 60 seconds.
        middlewares.push(createAuthMiddlewareWithExistingTokenWithRollingSuffix("Bearer ".concat(accessToken), { interval: 60000 }));
    }
    else if (auth === null || auth === void 0 ? void 0 : auth.credentials) {
        debug('Using client credentials authentication');
        middlewares.push((0, sdk_client_v2_1.createAuthForClientCredentialsFlow)(__assign({ fetch: (_d = httpOptions.fetch) !== null && _d !== void 0 ? _d : isomorphic_fetch_1.default, host: DEFAULT_AUTH_HOST, projectKey: projectKey }, auth)));
    }
    else {
        debug('Assuming MC cookie-based authentication');
        middlewares.push(acceptJsonMiddleware); // MC endpoint doesn't like Accept: */*
    }
    if (shoppingListServiceProxy) {
        middlewares.push(createMcAuthProxyMiddleware({
            forwardTo: shoppingListServiceProxy,
            host: (_e = httpOptions.host) !== null && _e !== void 0 ? _e : DEFAULT_API_HOST,
            path: '/shopping-lists',
            projectKey: projectKey,
        }));
    }
    middlewares.push((0, sdk_client_v2_1.createHttpClient)(__assign(__assign(__assign({ host: DEFAULT_API_HOST }, api), httpOptions), { fetch: (_f = httpOptions.fetch) !== null && _f !== void 0 ? _f : isomorphic_fetch_1.default })));
    return (0, platform_sdk_1.createApiBuilderFromCtpClient)((0, sdk_client_v2_1.createClient)({ middlewares: middlewares })).withProjectKey({ projectKey: projectKey });
}
exports.createApiBuilder = createApiBuilder;
//# sourceMappingURL=createApiBuilder.js.map